import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Stage, Layer, Rect, Circle, Text, Image as KonvaImage, Transformer, Group, Line } from 'react-konva';
import { createTemplate, getTemplateSvg, updateTemplateMapping, getFonts, uploadFont } from '../api';
import { ALL_FIELDS } from '../fields';
import { unicodeToBijoy, isAnsiBengaliFont } from '../bijoyConverter';

// ID card fixed size: 55mm × 87mm at 96 DPI
const CARD_W = Math.round(55 * 3.7795275591); // 208
const CARD_H = Math.round(87 * 3.7795275591); // 329

const BLANK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="${CARD_W}" height="${CARD_H}" viewBox="0 0 ${CARD_W} ${CARD_H}"><rect width="${CARD_W}" height="${CARD_H}" fill="#ffffff" stroke="#cccccc" stroke-width="0.5"/></svg>`;

const FIELD_OPTIONS = [
  { value: '', label: 'None' },
  ...ALL_FIELDS.map((f) => ({ value: f.key, label: f.label })),
  { value: 'student_photo', label: 'Photo' },
];

const FONT_OPTIONS = [
  'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Courier New',
  'Kalpurush', 'Siyam Rupali', 'Solaimanlipi', 'Nikosh', 'SutonnyMJ', 'Rupali',
];

const GRID_OPTIONS = [1, 2, 5, 10];
const SNAP_THRESHOLD = 6; // px in SVG units — within this distance, snap to guide

const getFieldLabel = (v) => FIELD_OPTIONS.find((o) => o.value === v)?.label ?? v ?? 'Unbound';

const parseSvgLength = (val) => {
  if (!val) return 0;
  const s = String(val).trim();
  if (s.endsWith('mm')) return parseFloat(s) * 3.7795275591;
  if (s.endsWith('cm')) return parseFloat(s) * 37.795275591;
  if (s.endsWith('pt')) return parseFloat(s) * 1.3333333;
  if (s.endsWith('in')) return parseFloat(s) * 96;
  return parseFloat(s) || 0;
};

const readSvgCoordSpace = (svgEl) => {
  const vb = svgEl.getAttribute('viewBox');
  if (vb) {
    const parts = vb.trim().split(/[\s,]+/).map(parseFloat);
    if (parts.length >= 4 && parts[2] > 0 && parts[3] > 0)
      return { width: parts[2], height: parts[3] };
  }
  const w = parseSvgLength(svgEl.getAttribute('width'));
  const h = parseSvgLength(svgEl.getAttribute('height'));
  if (w > 0 && h > 0) return { width: w, height: h };
  return null;
};

const snapTo = (v, grid) => Math.round(v / grid) * grid;

// The backend PDF exporter (_svg_fix_for_pdf) drops dominant-baseline="hanging"
// and instead pushes the text baseline down by 0.75 × font-size. So on the
// printed card the alphabetic baseline lands at  top + 0.75 × font-size.
// We mirror that EXACT formula in both the canvas and the live preview so the
// editor is truly WYSIWYG with the generated PDF.
const PDF_BASELINE_RATIO = 0.75;
const parseFontSize = (v) => {
  const n = parseFloat(String(v ?? '').replace(/px$/, ''));
  return isFinite(n) && n > 0 ? n : 14; // 14 == backend default in _svg_fix_for_pdf
};

// ── Demo data used for live preview ─────────────────────────────────────────
const DEMO_DATA = {
  student_name: 'Ahmed Rahman',
  name:         'Ahmed Rahman',
  student_id:   '2024-001',
  father_name:  'Abdul Rahman',
  mother_name:  'Fatema Begum',
  class_name:   'Class X',
  blood_group:  'B+',
  address:      '12 Mirpur Road, Dhaka',
  date_of_birth:'01 Jan 2008',
  mobile_number:'+880 1712-345678',
  session:      '2023-24',
  department:   'Science',
  designation:  'Student',
  husband_name: 'N/A',
  section:      'A',
  roll:         '15',
  index_no:        '123456',
  nid_no:          '1990123456789',
  joining_date:    '01 Jan 2023',
  guardians_mobile:'+880 1812-345678',
  registration_no: 'R-2024-015',
};

// Bengali demo values — used for Bengali fonts. ANSI/Bijoy fonts get these
// transliterated to Bijoy bytes; Latin fonts fall back to the English set above.
const DEMO_DATA_BN = {
  student_name: 'মোহাম্মদ রহমান', name: 'মোহাম্মদ রহমান',
  student_id:   '২০২৪-০০১',
  father_name:  'আব্দুল রহমান', mother_name: 'ফাতেমা বেগম',
  class_name:   'দশম', blood_group: 'বি+',
  address:      'মিরপুর, ঢাকা', date_of_birth: '০১ জানু ২০০৮',
  mobile_number:'০১৭১২৩৪৫৬৭৮', session: '২০২৩-২৪',
  department:   'বিজ্ঞান', designation: 'শিক্ষার্থী',
  husband_name: 'প্রযোজ্য নয়', section: 'ক', roll: '১৫',
  index_no:        '১২৩৪৫৬', nid_no: '১৯৯০১২৩৪৫৬৭৮৯',
  joining_date:    '০১ জানু ২০২৩', guardians_mobile: '০১৮১২৩৪৫৬৭৮',
  registration_no: 'আর-২০২৪-০১৫',
};

const LATIN_FONTS = new Set(['arial', 'helvetica', 'times new roman', 'georgia', 'courier new']);
const isLatinFont = (f) => LATIN_FONTS.has(String(f || '').toLowerCase().trim());

// Pick the demo value appropriate to the selected font so the preview matches
// the printed card: ANSI Bijoy fonts get Bijoy-encoded Bengali, Latin fonts get
// the English sample, Unicode Bengali fonts get Bengali as-is.
function demoValueForFont(fieldName, fontFamily) {
  const bn = DEMO_DATA_BN[fieldName];
  const en = DEMO_DATA[fieldName];
  if (isAnsiBengaliFont(fontFamily)) return bn ? unicodeToBijoy(bn) : (en || '');
  if (isLatinFont(fontFamily)) return en || bn || '';
  return bn || en || '';
}

// Read the effective font-family from an element's inline style or attribute.
function readFontFamily(el) {
  const st = (el && el.getAttribute && el.getAttribute('style')) || '';
  const m = st.match(/font-family\s*:\s*([^;]+)/i);
  if (m) return m[1].trim().replace(/['"]/g, '');
  return (el && el.getAttribute && el.getAttribute('font-family')) || '';
}

// Fill an existing SVG placeholder element in place (mirrors the backend) so
// the preview reflects what the generated card will show.
function fillExistingElement(el, fieldName, style, isImage) {
  const XLINK = 'http://www.w3.org/1999/xlink';
  if (isImage) {
    const img = el.tagName.toLowerCase() === 'image' ? el : el.querySelector('image');
    if (img) {
      img.setAttributeNS(XLINK, 'href', PHOTO_PLACEHOLDER_URI);
      img.setAttribute('href', PHOTO_PLACEHOLDER_URI);
    }
    return;
  }
  const tag = el.tagName.toLowerCase();
  const tgt = (tag === 'text' || tag === 'tspan') ? el : el.querySelector('text, tspan');
  if (!tgt) return;
  const font = (style && style.font_family) || readFontFamily(tgt) || readFontFamily(el);
  const value = demoValueForFont(fieldName, font);
  if (!value) return;
  const tspans = tgt.querySelectorAll ? tgt.querySelectorAll('tspan') : [];
  if (tspans && tspans.length) {
    tspans[0].textContent = value;
    for (let k = 1; k < tspans.length; k++) tspans[k].textContent = '';
  } else {
    tgt.textContent = value;
  }
}

// Tiny person silhouette as an inline SVG data URI for the photo placeholder
const PHOTO_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#d1d5db"/><circle cx="50" cy="36" r="20" fill="#9ca3af"/><ellipse cx="50" cy="85" rx="32" ry="26" fill="#9ca3af"/></svg>`;
const PHOTO_PLACEHOLDER_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(PHOTO_PLACEHOLDER_SVG)}`;

/**
 * Builds a preview SVG string by injecting demo data into the template.
 * Mirrors the backend render_card_svg logic so placement is accurate.
 */
function buildPreviewSvg(svgContent, mapping) {
  if (!svgContent) return null;
  const NS = 'http://www.w3.org/2000/svg';
  const XLINK = 'http://www.w3.org/1999/xlink';
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, 'image/svg+xml');
    const root = doc.querySelector('svg');
    if (!root) return null;

    // Collect IDs already in the SVG so we only inject NEW elements
    const existingIds = new Set();
    root.querySelectorAll('[id]').forEach((el) => existingIds.add(el.id));

    const getOrCreateDefs = () => {
      let defs = root.querySelector('defs');
      if (!defs) {
        defs = doc.createElementNS(NS, 'defs');
        root.insertBefore(defs, root.firstChild);
      }
      return defs;
    };

    for (const [id, entry] of Object.entries(mapping)) {
      const pos = entry.position || {};
      const x  = parseFloat(pos.x      || 0);
      const y  = parseFloat(pos.y      || 0);
      const w  = parseFloat(pos.width  || 60);
      const h  = parseFloat(pos.height || 20);
      const fieldName  = entry.field_name;
      const style      = entry.style || {};
      const isImage    = entry.element_type === 'image';
      const isCircle   = isImage && entry.shape === 'circle';

      // Placeholder already present in the SVG → fill it in place (matches backend).
      if (existingIds.has(id)) {
        const safeId = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape(id) : id;
        const exEl = root.querySelector(`[id="${safeId}"]`);
        if (exEl) fillExistingElement(exEl, fieldName, style, isImage);
        continue;
      }

      if (isImage) {
        // Photo placeholder: embed the silhouette image
        const clipId = `prev_clip_${id}`;
        if (isCircle) {
          const defs = getOrCreateDefs();
          const cp = doc.createElementNS(NS, 'clipPath');
          cp.setAttribute('id', clipId);
          const cc = doc.createElementNS(NS, 'circle');
          cc.setAttribute('cx', String(x + w / 2));
          cc.setAttribute('cy', String(y + h / 2));
          cc.setAttribute('r',  String(Math.min(w, h) / 2));
          cp.appendChild(cc);
          defs.appendChild(cp);
        }
        const img = doc.createElementNS(NS, 'image');
        img.setAttribute('x',      String(x));
        img.setAttribute('y',      String(y));
        img.setAttribute('width',  String(w));
        img.setAttribute('height', String(h));
        img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        img.setAttributeNS(XLINK, 'href', PHOTO_PLACEHOLDER_URI);
        img.setAttribute('href',           PHOTO_PLACEHOLDER_URI);
        if (isCircle) img.setAttribute('clip-path', `url(#${clipId})`);
        root.appendChild(img);
        continue;
      }

      // Text element
      const value = demoValueForFont(fieldName, style.font_family) || '';
      if (!value) continue;
      const align = style.text_align || 'left';
      const anchorMap = { left: 'start', center: 'middle', right: 'end' };
      const textAnchor = anchorMap[align] || 'start';
      // Adjust x so the anchor point matches the field box boundaries
      const textX = align === 'center' ? x + w / 2 : align === 'right' ? x + w : x;

      // Match the PDF exactly: alphabetic baseline at top + 0.75 × font-size,
      // with NO dominant-baseline (which Inkscape ignores and browsers read
      // differently — the original source of the "text is up/down" mismatch).
      const previewFs = parseFontSize(style.font_size);
      const baselineY = y + PDF_BASELINE_RATIO * previewFs;
      const el = doc.createElementNS(NS, 'text');
      el.setAttribute('x',           String(textX));
      el.setAttribute('y',           String(baselineY));
      el.setAttribute('text-anchor', textAnchor);
      const parts = [];
      if (style.font_family) parts.push(`font-family:${style.font_family}`);
      if (style.font_size)   parts.push(`font-size:${String(style.font_size).replace(/px$/, '')}px`);
      if (style.font_weight && style.font_weight !== 'normal') parts.push(`font-weight:${style.font_weight}`);
      if (style.fill)        parts.push(`fill:${style.fill}`);
      if (parts.length)      el.setAttribute('style', parts.join(';'));
      el.textContent = value;
      root.appendChild(el);
    }

    return new XMLSerializer().serializeToString(doc);
  } catch {
    return null;
  }
}

// Convert a CSS color (rgb/rgba/hex) to #rrggbb for the color inputs. Returns '' for none/transparent.
function colorToHex(c) {
  if (!c) return '';
  const s = String(c).trim().toLowerCase();
  if (!s || s === 'none' || s === 'transparent') return '';
  if (s[0] === '#') {
    if (s.length === 4) return '#' + [1, 2, 3].map((i) => s[i] + s[i]).join('');
    return s.slice(0, 7);
  }
  const m = s.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const p = m[1].split(',').map((x) => parseFloat(x.trim()));
    if (p.length > 3 && p[3] === 0) return '';
    const hex = (n) => Math.max(0, Math.min(255, Math.round(n || 0))).toString(16).padStart(2, '0');
    return '#' + hex(p[0]) + hex(p[1]) + hex(p[2]);
  }
  return '';
}

// Parse the template SVG into positioned elements (labels + vectors), in card
// (viewBox) coordinates, with their computed style. Uses a hidden mount +
// getBBox/CTM so geometry is exact regardless of transforms — this is what lets
// the editor snap to, and match the style of, the artwork baked into the template.
function extractTemplateElements(svgContent) {
  if (!svgContent || typeof document === 'undefined') return [];
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = 'position:absolute;left:-99999px;top:0;width:1200px;height:1200px;visibility:hidden;pointer-events:none;';
  host.innerHTML = svgContent;
  const root = host.querySelector('svg');
  if (!root) return [];
  document.body.appendChild(host);
  const out = [];
  try {
    const rootCTM = root.getScreenCTM();
    if (!rootCTM) return [];
    const inv = rootCTM.inverse();
    // Canvas draws the artwork at (0,0), so shift coords by the viewBox origin
    // to match (handles templates whose viewBox min-x/min-y is non-zero).
    const vb = root.viewBox && root.viewBox.baseVal;
    const vbx = vb ? vb.x : 0;
    const vby = vb ? vb.y : 0;
    const toCard = (el, x, y) => {
      const ctm = el.getScreenCTM();
      if (!ctm) return null;
      const m = inv.multiply(ctm);
      return { x: m.a * x + m.c * y + m.e - vbx, y: m.b * x + m.d * y + m.f - vby };
    };
    root.querySelectorAll('text, rect, circle, ellipse, path, polygon, polyline, line').forEach((el) => {
      const tag = el.tagName.toLowerCase();
      let bb;
      try { bb = el.getBBox(); } catch (e) { return; }
      if (!bb || (bb.width === 0 && bb.height === 0)) return;
      const corners = [
        toCard(el, bb.x, bb.y),
        toCard(el, bb.x + bb.width, bb.y),
        toCard(el, bb.x + bb.width, bb.y + bb.height),
        toCard(el, bb.x, bb.y + bb.height),
      ];
      if (corners.some((c) => !c)) return;
      const xs = corners.map((c) => c.x);
      const ys = corners.map((c) => c.y);
      const x = Math.min.apply(null, xs);
      const y = Math.min.apply(null, ys);
      const w = Math.max.apply(null, xs) - x;
      const h = Math.max.apply(null, ys) - y;
      const isText = tag === 'text';
      const cs = window.getComputedStyle(el);
      let baseline = null;
      if (isText) {
        try {
          const p = el.getStartPositionOfChar(0);
          const pc = toCard(el, p.x, p.y);
          if (pc) baseline = pc.y;
        } catch (e) { /* no chars */ }
      }
      let fill = colorToHex(cs.fill);
      if (!fill) fill = colorToHex(cs.stroke);
      out.push({
        type: isText ? 'text' : 'vector',
        x, y, w, h,
        cx: x + w / 2, cy: y + h / 2,
        baseline,
        fill,
        fontSize: isText ? Math.round((parseFloat(cs.fontSize) || 0) * 100) / 100 : null,
        fontFamily: isText ? (cs.fontFamily || '').split(',')[0].replace(/['"]/g, '').trim() : null,
        fontWeight: isText ? cs.fontWeight : null,
      });
    });
  } finally {
    if (host.parentNode) document.body.removeChild(host);
  }
  return out;
}

function VisualTemplateEditor({ template, onClose, onSaved }) {
  const isNew = !template?.id;

  const [templateName, setTemplateName] = useState(template?.name || 'Untitled Template');
  const [mapping, setMapping] = useState(template?.mapping_json || {});
  const [svgContent, setSvgContent] = useState(isNew ? BLANK_SVG : null);
  const [svgImage, setSvgImage] = useState(null);
  const [silhouetteImg, setSilhouetteImg] = useState(null);
  const [svgDimensions, setSvgDimensions] = useState({ width: CARD_W, height: CARD_H });
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(2);
  const [zoomInput, setZoomInput] = useState('200');
  const [gridSize, setGridSize] = useState(1);
  const [showGrid, setShowGrid] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(null);       // {x, y} in SVG units
  const [dragLive, setDragLive] = useState(null);   // {x, y, w, h} while dragging
  const [guides, setGuides] = useState([]);          // snap guide lines to show

  // Template-aware features: parsed template elements (labels/vectors) used for
  // smart alignment snapping and the "match style" eyedropper.
  const [templateEls, setTemplateEls] = useState([]); // [{type,x,y,w,h,cx,cy,baseline,fill,fontSize,fontFamily,fontWeight}]
  const [snapToTemplate, setSnapToTemplate] = useState(true);
  const [pickMode, setPickMode] = useState(false);    // eyedropper active
  const [hoverEl, setHoverEl] = useState(null);        // template element under cursor in pick mode

  // Uploaded fonts
  const [uploadedFonts, setUploadedFonts] = useState([]);
  const [fontUploading, setFontUploading] = useState(false);
  const fontInputRef = useRef(null);

  const stageRef = useRef(null);
  const transformerRef = useRef(null);
  const containerRef = useRef(null);

  // Sync zoom input when zoom changes from wheel/buttons
  useEffect(() => { setZoomInput(String(Math.round(zoom * 100))); }, [zoom]);

  // Load uploaded fonts and inject @font-face so Konva preview can render them
  useEffect(() => {
    getFonts()
      .then((r) => {
        setUploadedFonts(r.data || []);
        (r.data || []).forEach((font) => {
          const styleId = `custom-font-${font.name}`;
          if (!document.getElementById(styleId)) {
            const s = document.createElement('style');
            s.id = styleId;
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://185.208.206.127/api').replace(/\/api$/, '');
            s.textContent = `@font-face { font-family: '${font.name}'; src: url('${baseUrl}/api/fonts/${font.filename}'); }`;
            document.head.appendChild(s);
          }
        });
      })
      .catch(() => {});
  }, []);

  // Load SVG
  useEffect(() => {
    setTemplateName(template?.name || 'Untitled Template');
    setMapping(template?.mapping_json || {});
    setSelectedId(null);
    setSvgImage(null);
    if (template?.id) {
      let active = true;
      getTemplateSvg(template.id)
        .then((r) => { if (active) { setSvgContent(r.data); setError(''); } })
        .catch(() => { if (active) setError('Unable to load SVG.'); });
      return () => { active = false; };
    }
    setSvgContent(BLANK_SVG);
    setSvgDimensions({ width: CARD_W, height: CARD_H });
  }, [template]);

  // Parse SVG coordinate space and rasterise for Konva
  useEffect(() => {
    if (!svgContent) return;
    const parser = new DOMParser();
    const svgEl = parser.parseFromString(svgContent, 'image/svg+xml').querySelector('svg');
    if (svgEl) {
      const dims = readSvgCoordSpace(svgEl);
      if (dims) setSvgDimensions(dims);
    }
    const img = new window.Image();
    img.onload = () => setSvgImage(img);
    img.onerror = () => setError('Unable to render template image.');
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgContent)}`;
  }, [svgContent]);

  // Load the photo silhouette once so photo placeholders render realistically
  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setSilhouetteImg(img);
    img.src = PHOTO_PLACEHOLDER_URI;
  }, []);

  // Parse the template's own elements (labels & vectors) for smart snapping and
  // the eyedropper. Deferred a tick so layout/fonts settle before getBBox.
  useEffect(() => {
    if (!svgContent) { setTemplateEls([]); return; }
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) setTemplateEls(extractTemplateElements(svgContent)); }, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [svgContent]);

  // Wire Transformer
  useEffect(() => {
    const tr = transformerRef.current;
    const st = stageRef.current;
    if (!tr || !st) return;
    const node = selectedId ? st.findOne(`#${selectedId}`) : null;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
  }, [selectedId, mapping, svgImage]);

  // Keyboard handler: arrow nudge + delete + copy
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;

      const step = e.shiftKey ? 10 : gridSize;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        setMapping((p) => { const c = { ...p }; delete c[selectedId]; return c; });
        setSelectedId(null);
        return;
      }

      const dirs = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (dirs[e.key]) {
        e.preventDefault();
        const [dx, dy] = dirs[e.key];
        setMapping((p) => {
          const pos = p[selectedId]?.position ?? {};
          const w = parseFloat(pos.width ?? 60);
          const h = parseFloat(pos.height ?? 20);
          return {
            ...p,
            [selectedId]: {
              ...p[selectedId],
              position: {
                ...pos,
                x: Math.max(0, Math.min(cardW - w, (parseFloat(pos.x) || 0) + dx)),
                y: Math.max(0, Math.min(cardH - h, (parseFloat(pos.y) || 0) + dy)),
              },
            },
          };
        });
      }

      // Ctrl+D / Cmd+D — duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        duplicateSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, gridSize]); // eslint-disable-line

  const { width: cardW, height: cardH } = svgDimensions;

  // ── Snap guides ─────────────────────────────────────────────────────────────

  // Guides shown while dragging: card center/edges + (optionally) the template's
  // own element edges, centers, and TEXT BASELINES — so a value box lines up with
  // its label precisely. baseOff = the box's baseline offset (0.75 × font size).
  const computeGuides = useCallback((x, y, w, h, baseOff = 0) => {
    const lines = [];
    const near = (a, b) => Math.abs(a - b) < SNAP_THRESHOLD;
    const refX = [x, x + w / 2, x + w];
    const refY = [y, y + h / 2, y + h];
    const refBase = baseOff ? y + baseOff : null;
    const vT = [0, cardW / 2, cardW];
    const hT = [0, cardH / 2, cardH];
    const tplBaselines = [];
    if (snapToTemplate) {
      for (const t of templateEls) {
        vT.push(t.x, t.cx, t.x + t.w);
        hT.push(t.y, t.cy, t.y + t.h);
        if (t.baseline != null) tplBaselines.push(t.baseline);
      }
    }
    for (const tx of vT) if (refX.some((r) => near(r, tx))) lines.push({ type: 'V', x: tx });
    for (const ty of hT) if (refY.some((r) => near(r, ty))) lines.push({ type: 'H', y: ty });
    for (const by of tplBaselines) {
      if ((refBase != null && near(refBase, by)) || refY.some((r) => near(r, by))) lines.push({ type: 'H', y: by, baseline: true });
    }
    return lines;
  }, [cardW, cardH, templateEls, snapToTemplate]);

  // Snap a box to the single nearest target on each axis (card + template).
  // Vertical refs include the box baseline so it can lock onto a label's baseline.
  const applySnap = useCallback((x, y, w, h, grid, baseOff = 0) => {
    let sx = snapTo(x, grid), sy = snapTo(y, grid);
    const vT = [0, cardW / 2, cardW];
    const hT = [0, cardH / 2, cardH];
    if (snapToTemplate) {
      for (const t of templateEls) {
        vT.push(t.x, t.cx, t.x + t.w);
        hT.push(t.y, t.cy, t.y + t.h);
        if (t.baseline != null) hT.push(t.baseline);
      }
    }
    let bestDX = SNAP_THRESHOLD, dx = 0;
    for (const t of vT) for (const ref of [sx, sx + w / 2, sx + w]) {
      const d = t - ref;
      if (Math.abs(d) < bestDX) { bestDX = Math.abs(d); dx = d; }
    }
    sx += dx;
    const yrefs = baseOff ? [sy, sy + h / 2, sy + h, sy + baseOff] : [sy, sy + h / 2, sy + h];
    let bestDY = SNAP_THRESHOLD, dy = 0;
    for (const t of hT) for (const ref of yrefs) {
      const d = t - ref;
      if (Math.abs(d) < bestDY) { bestDY = Math.abs(d); dy = d; }
    }
    sy += dy;
    return { x: Math.max(0, Math.min(cardW - w, sx)), y: Math.max(0, Math.min(cardH - h, sy)) };
  }, [cardW, cardH, templateEls, snapToTemplate]);

  // Baseline offset for an element (text baseline sits 0.75×fontsize below its top).
  const baseOffOf = (id) =>
    mapping[id]?.element_type === 'image' ? 0 : PDF_BASELINE_RATIO * parseFontSize(mapping[id]?.style?.font_size);

  // Find the template element under a card-space point (smallest, text preferred).
  const findTemplateElAt = useCallback((px, py) => {
    let best = null, bestScore = Infinity;
    for (const t of templateEls) {
      if (px >= t.x && px <= t.x + t.w && py >= t.y && py <= t.y + t.h) {
        const score = Math.max(1, t.w * t.h) * (t.type === 'text' ? 0.4 : 1);
        if (score < bestScore) { bestScore = score; best = t; }
      }
    }
    return best;
  }, [templateEls]);

  // Copy a template element's style onto the selected field. Text → font + size +
  // weight + color; vector → color only (per the brief: vectors give color only).
  const applyMatchStyle = (t) => {
    if (!selectedId || !t) return;
    setMapping((p) => {
      const cur = p[selectedId];
      if (!cur || cur.element_type === 'image') return p;
      const style = { ...(cur.style || {}) };
      if (t.type === 'text') {
        if (t.fontFamily) style.font_family = t.fontFamily;
        if (t.fontSize) style.font_size = String(t.fontSize);
        if (t.fontWeight) style.font_weight = (t.fontWeight === 'bold' || parseInt(t.fontWeight, 10) >= 600) ? 'bold' : 'normal';
        if (t.fill) style.fill = t.fill;
      } else if (t.fill) {
        style.fill = t.fill;
      }
      return { ...p, [selectedId]: { ...cur, style } };
    });
  };

  // ── Actions ──────────────────────────────────────────────────────────────────

  const addPlaceholder = (elementType, fieldName, defaultW, defaultH) => {
    const id = `${elementType}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setMapping((prev) => ({
      ...prev,
      [id]: {
        element_type: elementType,
        field_name: fieldName || '',
        shape: 'rect',
        position: {
          x: Math.round(cardW / 2 - defaultW / 2),
          y: Math.round(cardH / 2 - defaultH / 2),
          width: defaultW,
          height: defaultH,
        },
        style: elementType === 'image'
          ? {}
          : { font_family: 'Arial', font_size: '14', font_weight: 'normal', fill: '#000000' },
      },
    }));
    setSelectedId(id);
  };

  const duplicateSelected = () => {
    if (!selectedId || !mapping[selectedId]) return;
    const src = mapping[selectedId];
    const newId = `${src.element_type}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const pos = src.position ?? {};
    setMapping((p) => ({
      ...p,
      [newId]: {
        ...src,
        position: { ...pos, x: (parseFloat(pos.x) || 0) + 10, y: (parseFloat(pos.y) || 0) + 10 },
      },
    }));
    setSelectedId(newId);
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const factor = e.evt.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom((z) => parseFloat(Math.max(0.2, Math.min(10, z * factor)).toFixed(2)));
  };

  const handleMouseMove = (e) => {
    const pos = e.target.getStage().getPointerPosition();
    if (!pos) return;
    const px = pos.x / zoom, py = pos.y / zoom;
    setCursor({ x: Math.round(px), y: Math.round(py) });
    if (pickMode) setHoverEl(findTemplateElAt(px, py));
  };

  const handleDragMove = (id) => (e) => {
    const node = e.target;
    const pos = mapping[id]?.position ?? {};
    const w = parseFloat(pos.width ?? 60);
    const h = parseFloat(pos.height ?? 20);
    const rawX = node.x(), rawY = node.y();
    setDragLive({ id, x: Math.round(rawX), y: Math.round(rawY), w: Math.round(w), h: Math.round(h) });
    setGuides(computeGuides(rawX, rawY, w, h, baseOffOf(id)));
  };

  const handleDragEnd = (id) => (e) => {
    const node = e.target;
    const pos = mapping[id]?.position ?? {};
    const w = parseFloat(pos.width ?? 60);
    const h = parseFloat(pos.height ?? 20);
    const snapped = applySnap(node.x(), node.y(), w, h, gridSize, baseOffOf(id));
    node.x(snapped.x);
    node.y(snapped.y);
    setDragLive(null);
    setGuides([]);
    setMapping((prev) => ({
      ...prev,
      [id]: { ...prev[id], position: { ...pos, ...snapped } },
    }));
  };

  const handleTransformEnd = (id) => (e) => {
    const node = e.target;
    const sx = node.scaleX(), sy = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    setMapping((prev) => {
      const oldPos = prev[id]?.position ?? {};
      const oldW = parseFloat(oldPos.width ?? 60);
      const oldH = parseFloat(oldPos.height ?? 20);
      const newW = snapTo(Math.max(10, Math.round(oldW * sx)), gridSize);
      const newH = snapTo(Math.max(8, Math.round(oldH * sy)), gridSize);
      const snappedPos = applySnap(Math.round(node.x()), Math.round(node.y()), newW, newH, gridSize, baseOffOf(id));
      return {
        ...prev,
        [id]: { ...prev[id], position: { ...snappedPos, width: newW, height: newH } },
      };
    });
  };

  const setField = (val) =>
    selectedId && setMapping((p) => ({ ...p, [selectedId]: { ...p[selectedId], field_name: val } }));

  const setShape = (val) =>
    selectedId && setMapping((p) => ({ ...p, [selectedId]: { ...p[selectedId], shape: val } }));

  const setStyle = (k, v) =>
    selectedId && setMapping((p) => ({
      ...p,
      [selectedId]: { ...p[selectedId], style: { ...p[selectedId].style, [k]: v } },
    }));

  const setPos = (k, v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return;
    setMapping((p) => ({
      ...p,
      [selectedId]: {
        ...p[selectedId],
        position: { ...p[selectedId].position, [k]: snapTo(num, gridSize) },
      },
    }));
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    setMapping((p) => { const c = { ...p }; delete c[selectedId]; return c; });
    setSelectedId(null);
  };

  const handleFontUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFontUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadFont(fd);
      const font = res.data;
      setUploadedFonts((prev) => [...prev.filter((f) => f.name !== font.name), font]);
      // Inject @font-face so the preview renders immediately
      const styleId = `custom-font-${font.name}`;
      if (!document.getElementById(styleId)) {
        const s = document.createElement('style');
        s.id = styleId;
        const baseUrl = (process.env.REACT_APP_API_URL || 'http://185.208.206.127/api').replace(/\/api$/, '');
        s.textContent = `@font-face { font-family: '${font.name}'; src: url('${baseUrl}/api/fonts/${font.filename}'); }`;
        document.head.appendChild(s);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Font upload failed.');
    } finally {
      setFontUploading(false);
      if (fontInputRef.current) fontInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const response = isNew
        ? await createTemplate({ name: templateName.trim() || 'Untitled', svg_content: svgContent, mapping_json: mapping })
        : await updateTemplateMapping(template.id, mapping);
      onSaved(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to save.');
    } finally {
      setSaving(false);
    }
  };

  const commitZoomInput = () => {
    const pct = parseInt(zoomInput, 10);
    if (!isNaN(pct) && pct >= 20 && pct <= 1000)
      setZoom(pct / 100);
    else
      setZoomInput(String(Math.round(zoom * 100)));
  };

  // Recompute preview SVG whenever mapping or template changes
  const previewSvg = useMemo(() => {
    if (!showPreview || !svgContent) return null;
    return buildPreviewSvg(svgContent, mapping);
  }, [showPreview, svgContent, mapping]);

  const previewDataUri = previewSvg
    ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(previewSvg)}`
    : null;

  const sel = mapping[selectedId];

  // ── Grid overlay lines ───────────────────────────────────────────────────────
  const gridLines = [];
  if (showGrid && gridSize > 1) {
    for (let x = gridSize; x < cardW; x += gridSize)
      gridLines.push(<Line key={`gv${x}`} points={[x, 0, x, cardH]} stroke="rgba(59,130,246,0.15)" strokeWidth={0.5 / zoom} listening={false} />);
    for (let y = gridSize; y < cardH; y += gridSize)
      gridLines.push(<Line key={`gh${y}`} points={[0, y, cardW, y]} stroke="rgba(59,130,246,0.15)" strokeWidth={0.5 / zoom} listening={false} />);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal fullscreen"
        onClick={(e) => e.stopPropagation()}
        ref={containerRef}
        style={{ width: '95vw', height: '95vh', maxWidth: 'unset', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h2 style={{ marginBottom: 4 }}>{isNew ? 'Create New Template' : 'Visual Template Editor'}</h2>
          {isNew ? (
            <input className="form-input" style={{ marginTop: 8, maxWidth: 360 }}
              value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" />
          ) : (
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
              Click to select · Drag to move · Resize handles · Arrow keys to nudge · Del to delete · Ctrl+D to duplicate · Eyedropper to match a template label
            </p>
          )}
        </div>

        {error && <div className="alert alert-error" style={{ margin: '0 16px', flexShrink: 0 }}>{error}</div>}

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* ── Canvas column ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Toolbar */}
            <div style={{
              display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
              padding: '6px 12px', borderBottom: '1px solid var(--border)',
              background: 'var(--surface)', flexShrink: 0,
            }}>
              {/* Zoom controls */}
              <button className="btn btn-sm btn-ghost" onClick={() => setZoom((z) => parseFloat(Math.max(0.2, z / 1.2).toFixed(2)))}>−</button>
              <input
                style={{
                  width: 52, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                  fontSize: 12, border: '1px solid var(--border)', borderRadius: 4,
                  padding: '2px 4px', background: 'var(--bg2)', color: 'var(--text)',
                }}
                value={zoomInput}
                onChange={(e) => setZoomInput(e.target.value)}
                onBlur={commitZoomInput}
                onKeyDown={(e) => e.key === 'Enter' && commitZoomInput()}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>%</span>
              <button className="btn btn-sm btn-ghost" onClick={() => setZoom((z) => parseFloat(Math.min(10, z * 1.2).toFixed(2)))}>+</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setZoom(2)}>Reset</button>

              <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />

              {/* Grid */}
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Grid:</span>
              {GRID_OPTIONS.map((g) => (
                <button
                  key={g}
                  className={`btn btn-sm ${gridSize === g ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ minWidth: 28 }}
                  onClick={() => setGridSize(g)}
                >
                  {g}
                </button>
              ))}
              <button
                className={`btn btn-sm ${showGrid ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setShowGrid((v) => !v)}
                title="Toggle grid overlay"
                style={{ fontSize: 14 }}
              >
                ⊞
              </button>
              <button
                className={`btn btn-sm ${snapToTemplate ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setSnapToTemplate((v) => !v)}
                title="Snap to the template's labels & artwork (edges, centers, text baselines)"
              >
                ⊹ Snap to template
              </button>

              <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />

              {/* Add elements */}
              <button className="btn btn-sm btn-outline" onClick={() => addPlaceholder('text', '', 80, 14)}>+ Text</button>
              <button className="btn btn-sm btn-outline" onClick={() => addPlaceholder('image', 'student_photo', 60, 75)}>+ Photo</button>
              {selectedId && (
                <button className="btn btn-sm btn-ghost" onClick={duplicateSelected} title="Ctrl+D">Duplicate</button>
              )}

              <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />
              <button
                className={`btn btn-sm ${showPreview ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setShowPreview((v) => !v)}
                title="Toggle live preview with demo data"
              >
                {showPreview ? '◉ Preview ON' : '◎ Preview'}
              </button>

              {/* Status */}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                {dragLive
                  ? `x:${dragLive.x} y:${dragLive.y} w:${dragLive.w} h:${dragLive.h}`
                  : cursor
                  ? `${cursor.x}, ${cursor.y}`
                  : `${Math.round(cardW)} × ${Math.round(cardH)}`}
              </span>
            </div>

            {/* Scrollable canvas */}
            <div style={{ flex: 1, overflow: 'auto', background: '#e9ecef', padding: 32 }}>
              <div style={{ display: 'inline-block', boxShadow: '0 2px 18px rgba(0,0,0,0.18)', borderRadius: 2 }}>
                <Stage
                  width={cardW * zoom}
                  height={cardH * zoom}
                  scaleX={zoom}
                  scaleY={zoom}
                  ref={stageRef}
                  onMouseDown={(e) => {
                    if (pickMode) {
                      const pp = e.target.getStage().getPointerPosition();
                      const t = pp ? findTemplateElAt(pp.x / zoom, pp.y / zoom) : null;
                      if (t) applyMatchStyle(t);
                      setPickMode(false);
                      setHoverEl(null);
                      return;
                    }
                    if (e.target === e.target.getStage()) setSelectedId(null);
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => { setCursor(null); if (pickMode) setHoverEl(null); }}
                  onWheel={handleWheel}
                  style={{ display: 'block', cursor: pickMode ? 'copy' : 'crosshair' }}
                >
                  <Layer>
                    {/* Template background */}
                    {svgImage && (
                      <KonvaImage image={svgImage} x={0} y={0} width={cardW} height={cardH} listening={false} />
                    )}

                    {/* Grid overlay */}
                    {gridLines}

                    {/* Card center guides (always shown faintly) */}
                    <Line points={[cardW / 2, 0, cardW / 2, cardH]} stroke="rgba(99,102,241,0.2)" strokeWidth={0.5 / zoom} dash={[4 / zoom, 4 / zoom]} listening={false} />
                    <Line points={[0, cardH / 2, cardW, cardH / 2]} stroke="rgba(99,102,241,0.2)" strokeWidth={0.5 / zoom} dash={[4 / zoom, 4 / zoom]} listening={false} />

                    {/* Snap guides (shown while dragging). Green = aligning to a
                        template text baseline; amber = edge/center alignment. */}
                    {guides.map((g, i) =>
                      g.type === 'V'
                        ? <Line key={i} points={[g.x, 0, g.x, cardH]} stroke="#f59e0b" strokeWidth={1 / zoom} listening={false} />
                        : <Line key={i} points={[0, g.y, cardW, g.y]} stroke={g.baseline ? '#16a34a' : '#f59e0b'} strokeWidth={1 / zoom} listening={false} />
                    )}

                    {/* Eyedropper: highlight the template element under the cursor */}
                    {pickMode && hoverEl && (
                      <>
                        <Rect
                          x={hoverEl.x} y={hoverEl.y} width={hoverEl.w} height={hoverEl.h}
                          fill="rgba(245,158,11,0.12)" stroke="#f59e0b" strokeWidth={1.5 / zoom}
                          dash={[3 / zoom, 2 / zoom]} listening={false}
                        />
                        {hoverEl.baseline != null && (
                          <Line points={[hoverEl.x, hoverEl.baseline, hoverEl.x + hoverEl.w, hoverEl.baseline]}
                            stroke="#16a34a" strokeWidth={1 / zoom} listening={false} />
                        )}
                      </>
                    )}

                    {/* Placeholder elements — rendered to mirror the printed card */}
                    {Object.entries(mapping).map(([id, info]) => {
                      const x = parseFloat(info.position?.x ?? 0);
                      const y = parseFloat(info.position?.y ?? 0);
                      const w = parseFloat(info.position?.width ?? 60);
                      const h = parseFloat(info.position?.height ?? 20);
                      const isSelected = selectedId === id;
                      const isPhoto = info.element_type === 'image';
                      const isCircle = isPhoto && info.shape === 'circle';
                      const font = info.style?.font_family || 'Arial';
                      const fontSize = parseFontSize(info.style?.font_size); // real size, not clamped to the box
                      const align = isPhoto ? 'center' : (info.style?.text_align || 'left');
                      const fill = isPhoto ? '#2563eb' : (info.style?.fill || '#111111');
                      const strokeW = (isSelected ? 1.5 : 0.75) / zoom;
                      const dashArr = isSelected ? [] : [3 / zoom, 2 / zoom];

                      // Real demo value so what's on the canvas == what prints.
                      let dispText = '';
                      if (!isPhoto) {
                        const dv = info.field_name ? demoValueForFont(info.field_name, font) : '';
                        dispText = dv || getFieldLabel(info.field_name) || 'Text';
                      }

                      return (
                        <Group
                          key={id}
                          id={id}
                          x={x} y={y}
                          draggable
                          onClick={() => setSelectedId(id)}
                          onTap={() => setSelectedId(id)}
                          onDragStart={() => setSelectedId(id)}
                          onDragMove={handleDragMove(id)}
                          onDragEnd={handleDragEnd(id)}
                          onTransformEnd={handleTransformEnd(id)}
                        >
                          {isPhoto ? (
                            <>
                              {isCircle ? (
                                <Group clipFunc={(ctx) => { ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2, false); }}>
                                  {silhouetteImg
                                    ? <KonvaImage image={silhouetteImg} x={0} y={0} width={w} height={h} listening={false} />
                                    : <Rect width={w} height={h} fill="rgba(59,130,246,0.10)" listening={false} />}
                                </Group>
                              ) : (
                                silhouetteImg
                                  ? <KonvaImage image={silhouetteImg} x={0} y={0} width={w} height={h} listening={false} />
                                  : <Rect width={w} height={h} fill="rgba(59,130,246,0.10)" listening={false} />
                              )}
                              {isCircle ? (
                                <Circle
                                  x={w / 2} y={h / 2} radius={Math.min(w, h) / 2}
                                  stroke={isSelected ? '#2563eb' : '#3b82f6'}
                                  strokeWidth={strokeW} dash={dashArr}
                                />
                              ) : (
                                <Rect
                                  width={w} height={h}
                                  stroke={isSelected ? '#2563eb' : '#3b82f6'}
                                  strokeWidth={strokeW} dash={dashArr} cornerRadius={1}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              {/* faint field-bounds box for selection & hit-testing */}
                              <Rect
                                width={w} height={h}
                                fill={isSelected ? 'rgba(37,99,235,0.06)' : 'rgba(148,163,184,0.05)'}
                                stroke={isSelected ? '#2563eb' : '#cbd5e1'}
                                strokeWidth={strokeW}
                                dash={dashArr}
                                cornerRadius={1}
                              />
                              {/* real text: top-anchored, single line, overflows like the PDF.
                                  Explicit width/height keeps the transformer box == the field box. */}
                              <Text
                                text={dispText}
                                fontFamily={font}
                                fontSize={fontSize}
                                fontStyle={info.style?.font_weight || 'normal'}
                                fill={fill}
                                width={w} height={h}
                                x={0} y={0}
                                align={align}
                                verticalAlign="top"
                                wrap="none"
                                listening={false}
                              />
                            </>
                          )}
                        </Group>
                      );
                    })}

                    {/* Baseline guide for the selected text element. Drawn OUTSIDE the
                        groups (in card coords) so it never inflates the transformer box.
                        Follows the live drag position while dragging. */}
                    {(() => {
                      if (!selectedId) return null;
                      const selInfo = mapping[selectedId];
                      if (!selInfo || selInfo.element_type === 'image') return null;
                      const live = dragLive && dragLive.id === selectedId ? dragLive : null;
                      const bx = live ? live.x : parseFloat(selInfo.position?.x ?? 0);
                      const by = live ? live.y : parseFloat(selInfo.position?.y ?? 0);
                      const bw = live ? live.w : parseFloat(selInfo.position?.width ?? 60);
                      const fs = parseFontSize(selInfo.style?.font_size);
                      const baseY = by + PDF_BASELINE_RATIO * fs;
                      return (
                        <Line
                          points={[bx, baseY, bx + bw, baseY]}
                          stroke="#16a34a"
                          strokeWidth={1 / zoom}
                          dash={[3 / zoom, 2 / zoom]}
                          listening={false}
                        />
                      );
                    })()}

                    <Transformer
                      ref={transformerRef}
                      rotateEnabled={false}
                      anchorSize={7}
                      anchorCornerRadius={2}
                      anchorStroke="#2563eb"
                      anchorFill="#ffffff"
                      borderStroke="#2563eb"
                      borderDash={[3, 2]}
                      boundBoxFunc={(_, newBox) => {
                        if (selectedId && mapping[selectedId]?.shape === 'circle') {
                          const side = Math.max(10, Math.max(newBox.width, newBox.height));
                          return { ...newBox, width: side, height: side };
                        }
                        return { ...newBox, width: Math.max(10, newBox.width), height: Math.max(8, newBox.height) };
                      }}
                    />
                  </Layer>
                </Stage>
              </div>
            </div>
          </div>

          {/* ── Live Preview panel ── */}
          {showPreview && (
            <div style={{
              width: 260, flexShrink: 0,
              borderLeft: '1px solid var(--border)',
              overflow: 'auto',
              background: 'var(--bg)',
              display: 'flex', flexDirection: 'column',
            }}>
              <div style={{
                padding: '10px 14px', borderBottom: '1px solid var(--border)',
                fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--text-muted)',
                background: 'var(--surface)', flexShrink: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>Live Preview</span>
                <span style={{ fontWeight: 400, fontSize: '0.72rem', color: 'var(--accent)' }}>demo data</span>
              </div>

              {/* Card preview */}
              <div style={{ padding: 16, flexShrink: 0 }}>
                {previewDataUri ? (
                  <>
                    {/* Zoom controls */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 10 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}
                        onClick={() => setPreviewZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} title="Zoom out">−</button>
                      <span style={{ fontSize: 11, minWidth: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                        {Math.round(previewZoom * 100)}%
                      </span>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}
                        onClick={() => setPreviewZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))} title="Zoom in">+</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}
                        onClick={() => setPreviewZoom(1)} title="Reset zoom">⟲</button>
                      <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}
                        onClick={() => setPreviewOpen(true)} title="Open full preview">⤢</button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', overflow: 'auto', maxHeight: 360 }}>
                      <div
                        onClick={() => setPreviewOpen(true)}
                        title="Click to enlarge"
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 4,
                          overflow: 'hidden',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                          width: 200 * previewZoom,
                          aspectRatio: `${CARD_W} / ${CARD_H}`,
                          flexShrink: 0,
                          cursor: 'zoom-in',
                        }}
                      >
                        <img
                          src={previewDataUri}
                          alt="Card preview"
                          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain' }}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: 24 }}>
                    No template loaded
                  </div>
                )}
              </div>

              {/* Demo data table */}
              <div style={{ padding: '0 14px 14px', flexShrink: 0 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>
                  Demo values
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {Object.entries(mapping)
                    .filter(([ , e]) => e.element_type !== 'image' && e.field_name && (DEMO_DATA_BN[e.field_name] || DEMO_DATA[e.field_name]))
                    .map(([id, e]) => (
                      <div
                        key={id}
                        onClick={() => setSelectedId(id)}
                        style={{
                          display: 'flex', gap: 6, cursor: 'pointer',
                          padding: '3px 6px', borderRadius: 4,
                          background: selectedId === id ? 'var(--bg3)' : 'transparent',
                          fontSize: '0.78rem',
                        }}
                      >
                        <span style={{ color: 'var(--text-muted)', minWidth: 80, flexShrink: 0 }}>
                          {getFieldLabel(e.field_name)}
                        </span>
                        <span style={{ color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {DEMO_DATA_BN[e.field_name] || DEMO_DATA[e.field_name]}
                        </span>
                      </div>
                    ))
                  }
                  {Object.entries(mapping).filter(([ , e]) => e.element_type === 'image').map(([id, e]) => (
                    <div
                      key={id}
                      onClick={() => setSelectedId(id)}
                      style={{
                        display: 'flex', gap: 6, cursor: 'pointer',
                        padding: '3px 6px', borderRadius: 4,
                        background: selectedId === id ? 'var(--bg3)' : 'transparent',
                        fontSize: '0.78rem',
                      }}
                    >
                      <span style={{ color: 'var(--text-muted)', minWidth: 80, flexShrink: 0 }}>Photo</span>
                      <span style={{ color: 'var(--text)' }}>[ sample silhouette ]</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ padding: '8px 14px 14px', borderTop: '1px solid var(--border)', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                The preview now matches the generated PDF's text placement (same baseline math). Only fonts not installed on the server may still differ.
              </div>
            </div>
          )}

          {/* ── Full-screen preview popup ── */}
          {previewOpen && previewDataUri && (
            <div
              onClick={() => setPreviewOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.82)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32,
              }}
            >
              <img
                src={previewDataUri}
                alt="Card preview (full size)"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: '92vw', maxHeight: '92vh',
                  background: '#fff', borderRadius: 6,
                  boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
                  imageRendering: 'auto',
                }}
              />
              <button
                onClick={() => setPreviewOpen(false)}
                className="btn btn-sm"
                style={{ position: 'fixed', top: 20, right: 24, background: 'var(--surface)', color: 'var(--text)' }}
              >
                ✕ Close
              </button>
            </div>
          )}

          {/* ── Properties panel ── */}
          <div style={{
            width: 288, flexShrink: 0,
            borderLeft: '1px solid var(--border)',
            overflow: 'auto', padding: 16,
            background: 'var(--surface)',
          }}>
            {!sel ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', paddingTop: 48, lineHeight: 2, fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>◈</div>
                Select an element to edit its properties.
                <div style={{ marginTop: 16, fontSize: 11, lineHeight: 1.7, textAlign: 'left', background: 'var(--bg2)', borderRadius: 6, padding: '10px 12px' }}>
                  <strong>Tips</strong><br />
                  Arrow keys — nudge 1px<br />
                  Shift + Arrow — nudge 10px<br />
                  Ctrl + D — duplicate<br />
                  Del — delete selected<br />
                  Grid snap — set grid size above<br />
                  Yellow lines — snap guides<br />
                  Green line — text baseline (where it prints)
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{getFieldLabel(sel?.field_name)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {sel?.element_type}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={duplicateSelected} title="Ctrl+D">⧉</button>
                    <button className="btn btn-danger btn-sm" onClick={deleteSelected}>✕</button>
                  </div>
                </div>

                {/* Field binding */}
                <div>
                  <label className="form-label">Bind to field</label>
                  <select className="form-select" value={sel?.field_name || ''} onChange={(e) => setField(e.target.value)}>
                    {FIELD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {/* Match style from a template label/vector (eyedropper) */}
                {sel?.element_type !== 'image' && (
                  <div>
                    <button
                      type="button"
                      className={`btn btn-sm ${pickMode ? 'btn-primary' : 'btn-outline'}`}
                      style={{ width: '100%' }}
                      onClick={() => { setPickMode((v) => !v); setHoverEl(null); }}
                      disabled={templateEls.length === 0}
                      title={templateEls.length === 0 ? 'No template artwork detected to match' : 'Copy size & color from a label on the template'}
                    >
                      {pickMode ? '⊙ Click a label on the card…' : '⊙ Match style from template'}
                    </button>
                    {pickMode && (
                      <div style={{ marginTop: 6, fontSize: 11, lineHeight: 1.5, color: 'var(--text-muted)', background: 'var(--bg2)', borderRadius: 6, padding: '8px 10px' }}>
                        Click any <strong>text</strong> on the template to copy its font, size &amp; color. Click a <strong>logo/vector</strong> to copy its color only.
                      </div>
                    )}
                  </div>
                )}

                {/* Shape — image only */}
                {sel?.element_type === 'image' && (
                  <div>
                    <label className="form-label">Shape</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[{ value: 'rect', label: 'Rectangle' }, { value: 'circle', label: 'Circle' }].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`btn btn-sm ${(sel?.shape || 'rect') === opt.value ? 'btn-primary' : 'btn-ghost'}`}
                          style={{ flex: 1 }}
                          onClick={() => setShape(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Position & Size */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Position &amp; Size</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>grid: {gridSize}px</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { k: 'x', label: 'X' },
                      { k: 'y', label: 'Y' },
                      { k: 'width', label: 'W' },
                      { k: 'height', label: 'H' },
                    ].map(({ k, label }) => (
                      <div key={k}>
                        <label className="form-label">{label}</label>
                        <input
                          className="form-input"
                          type="number"
                          step={gridSize}
                          value={Math.round(parseFloat(sel?.position?.[k] ?? 0))}
                          onChange={(e) => setPos(k, e.target.value)}
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        />
                      </div>
                    ))}
                  </div>
                  {/* Quick align buttons */}
                  <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', width: '100%', marginBottom: 2 }}>Align to card:</span>
                    {[
                      { label: '⇤ Left', action: () => setPos('x', 0) },
                      { label: 'Center ↔', action: () => setPos('x', Math.round(cardW / 2 - parseFloat(sel?.position?.width ?? 60) / 2)) },
                      { label: 'Right ⇥', action: () => setPos('x', Math.round(cardW - parseFloat(sel?.position?.width ?? 60))) },
                      { label: '⇡ Top', action: () => setPos('y', 0) },
                      { label: 'Middle ↕', action: () => setPos('y', Math.round(cardH / 2 - parseFloat(sel?.position?.height ?? 20) / 2)) },
                      { label: 'Bottom ⇣', action: () => setPos('y', Math.round(cardH - parseFloat(sel?.position?.height ?? 20))) },
                    ].map(({ label, action }) => (
                      <button key={label} className="btn btn-ghost" style={{ fontSize: 10, padding: '2px 6px', height: 'auto' }} onClick={action}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text style */}
                {sel?.element_type !== 'image' && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Text Style</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Font family</span>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: 10, padding: '1px 6px', height: 'auto' }}
                            onClick={() => fontInputRef.current?.click()}
                            disabled={fontUploading}
                            title="Upload a custom font (.ttf/.otf)"
                          >
                            {fontUploading ? '…' : '+ Upload Font'}
                          </button>
                          <input
                            ref={fontInputRef}
                            type="file"
                            accept=".ttf,.otf,.woff,.woff2"
                            style={{ display: 'none' }}
                            onChange={handleFontUpload}
                          />
                        </label>
                        <select className="form-select" value={sel?.style?.font_family || ''} onChange={(e) => setStyle('font_family', e.target.value)}>
                          <option value="">Default</option>
                          <optgroup label="Built-in">
                            {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                          </optgroup>
                          {uploadedFonts.length > 0 && (
                            <optgroup label="Uploaded">
                              {uploadedFonts.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label className="form-label">Size (px)</label>
                          <input
                            className="form-input"
                            type="number"
                            min={6} max={200}
                            value={sel?.style?.font_size || '12'}
                            onChange={(e) => setStyle('font_size', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="form-label">Weight</label>
                          <select className="form-select" value={sel?.style?.font_weight || 'normal'} onChange={(e) => setStyle('font_weight', e.target.value)}>
                            <option value="normal">Normal</option>
                            <option value="bold">Bold</option>
                            <option value="italic">Italic</option>
                            <option value="bold italic">Bold Italic</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Alignment</label>
                        <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                          {[
                            { value: 'left',   icon: '⬤ ‒ ‒', title: 'Left' },
                            { value: 'center', icon: '‒ ⬤ ‒', title: 'Center' },
                            { value: 'right',  icon: '‒ ‒ ⬤', title: 'Right' },
                          ].map(({ value, icon, title }) => {
                            const active = (sel?.style?.text_align || 'left') === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                title={title}
                                onClick={() => setStyle('text_align', value)}
                                style={{
                                  flex: 1, padding: '6px 4px',
                                  background: active ? 'var(--accent)' : 'var(--bg2)',
                                  color: active ? '#fff' : 'var(--text-muted)',
                                  border: 'none',
                                  borderRight: value !== 'right' ? '1px solid var(--border)' : 'none',
                                  cursor: 'pointer', fontSize: 11, fontFamily: 'monospace',
                                  fontWeight: active ? 700 : 400,
                                }}
                              >
                                {icon}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <label className="form-label">Color</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            type="color"
                            value={sel?.style?.fill || '#000000'}
                            onChange={(e) => setStyle('fill', e.target.value)}
                            style={{ width: 40, height: 36, padding: 2, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', background: 'none' }}
                          />
                          <input
                            className="form-input"
                            type="text"
                            value={sel?.style?.fill || '#000000'}
                            onChange={(e) => setStyle('fill', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', marginRight: 'auto' }}>
            {Object.keys(mapping).length} field{Object.keys(mapping).length !== 1 ? 's' : ''} mapped
          </span>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Save mapping'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VisualTemplateEditor;
